
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import MapShell from '../MapShell';
import useUIPresentationMode from '../helpers/useUIPresentationMode';
import { fetchUsageData } from './fetchUsageData';
import { fetchMapLayers } from '../helpers/fetchMapLayers';
import { initialiseLayers } from '../helpers/initialiseLayers';

import UsageSideBarLeft from './UsageSideBarLeft';
import UsageMapComponent from './UsageMapComponent';
import UsageSideBarRight from './UsageSideBarRight';


export default function UsageLayout({ onLogout = () => { } }) {

  const { presentationModeSize, presentationModeiFrame } = useUIPresentationMode();

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const topLayerRef = useRef(null);

  const [pins, setPins] = useState([]);
  const [selectedPin, setSelectedPin] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [createdFilter, setCreatedFilter] = useState('30'); // days window: 'latest','30','60','180','365','all'

  const openRightSidebarRef = useRef(null);
  const closeRightSidebarRef = useRef(null);
  const didInitialRightCloseRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);

  const [layers, setLayers] = useState({ primary: [], secondary: [] });
  const [selectedBaseMap, setSelectedBaseMap] = useState('None');
  const [isParcelBoundariesVisible, setIsParcelBoundariesVisible] = useState(false);
  const [selectedStyles, setSelectedStyles] = useState(new Set());
  const [circleAnalysis, setCircleAnalysis] = useState({
    hasCircle: false,
    topPlans: [],
  });


  useEffect(() => {
    let isCancelled = false;

    async function load() {
      setIsLoading(true);
      setLoadError(null);
      try {
        const latestOnly = createdFilter === 'latest';
        const cutoff =
          latestOnly || createdFilter === 'all'
            ? null
            : (() => {
              const days = Number(createdFilter);
              if (Number.isNaN(days)) return null;
              const d = new Date();
              d.setDate(d.getDate() - days);
              return d;
            })();

        const data = await fetchUsageData({ cutoffDate: cutoff, latestOnly });
        if (!isCancelled) {
          setPins(data);
          const styleValues = Array.from(
            new Set(
              data
                .map((pin) => pin.style)
                .filter((style) => typeof style === 'string' && style.trim() !== '')
            )
          ).sort((a, b) => a.localeCompare(b));

          setSelectedStyles((current) => {
            if (!current || current.size === 0) {
              return new Set(styleValues);
            }

            const next = new Set();
            styleValues.forEach((style) => {
              if (current.has(style)) next.add(style);
            });

            styleValues.forEach((style) => {
              if (!current.has(style)) next.add(style);
            });

            return next;
          });

          setSelectedPin((currentPin) => {
            if (!currentPin) return null;
            return data.some((pin) => pin.id === currentPin.id) ? currentPin : null;
          });
        }
      } catch (err) {
        console.error('Failed to load usage data:', err);
        if (!isCancelled) setLoadError(err);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    }

    load();
    return () => { isCancelled = true; };
  }, [createdFilter]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    initialiseLayers({
      fetchMapLayers,
      mapRef: mapInstanceRef,
      topLayerRef,
      setLayers,
      setSelectedBaseMap,
      setIsParcelBoundariesVisible,
    });
  }, [mapReady]);

  const availableStyles = useMemo(() => Array.from(
    new Set(
      pins
        .map((pin) => pin.style)
        .filter((style) => typeof style === 'string' && style.trim() !== '')
    )
  ).sort((a, b) => a.localeCompare(b)), [pins]);

  // Marker setup depends on this array identity. Keep it stable while unrelated
  // state (such as the selected pin or an open sidebar) changes.
  const filteredPins = useMemo(() => pins.filter((pin) => {
    if (availableStyles.length > 0 && selectedStyles.size === 0) {
      return false;
    }
    if (!pin.style || availableStyles.length === 0) return true;
    return selectedStyles.has(pin.style);
  }), [availableStyles, pins, selectedStyles]);

  useEffect(() => {
    if (!selectedPin) return;
    const stillVisible = filteredPins.some((pin) => pin.id === selectedPin.id);
    if (!stillVisible) {
      setSelectedPin(null);
      closeRightSidebarRef.current?.();
    }
  }, [filteredPins, selectedPin]);

  const registerRightSidebarOpen = useCallback((fn) => {
    openRightSidebarRef.current = fn;
  }, []);

  const registerRightSidebarClose = useCallback((fn) => {
    closeRightSidebarRef.current = fn;
    if (!didInitialRightCloseRef.current) {
      fn();
      didInitialRightCloseRef.current = true;
    }
  }, []);

  const handleMapReady = useCallback(() => {
    setMapReady(true);
  }, []);

  const handlePinSelect = useCallback((pin) => {
    setSelectedPin(pin);
  }, []);

  const handleSelectionInvalidated = useCallback(() => {
    setSelectedPin(null);
    closeRightSidebarRef.current?.();
  }, []);

  const handleCircleAnalysisChange = useCallback((analysis) => {
    setCircleAnalysis(analysis);
  }, []);

  const handleForceRightSidebarOpen = useCallback(() => {
    openRightSidebarRef.current?.();
  }, []);

  return (
    <MapShell
      presentationModeSize={presentationModeSize}
      defaultRightOpen={false}
      autoCollapseByPresentationMode={false}
      onRequestRightSidebarOpen={registerRightSidebarOpen}
      onRequestRightSidebarClose={registerRightSidebarClose}
      leftContent={
        <UsageSideBarLeft
          isVisible
          filterValue={createdFilter}
          setFilterValue={setCreatedFilter}
          isLoading={isLoading}
          loadError={loadError}
          totalCount={pins.length}
          filteredCount={filteredPins.length}
          onLogout={onLogout}
          availableStyles={availableStyles}
          selectedStyles={selectedStyles}
          setSelectedStyles={setSelectedStyles}
          circleAnalysis={circleAnalysis}
          onClearCircle={() => {
            mapRef.current?.clearAnalysisCircle?.();
            setCircleAnalysis({ hasCircle: false, topPlans: [] });
          }}
          layers={layers}
          selectedBaseMap={selectedBaseMap}
          setSelectedBaseMap={setSelectedBaseMap}
          isParcelBoundariesVisible={isParcelBoundariesVisible}
          toggleParcelBoundaries={(visible) => {
            setIsParcelBoundariesVisible(visible);
            mapRef.current?.toggleParcelBoundaries?.(visible);
            const url = new URL(window.location);
            url.searchParams.set('pb', visible ? '1' : '0');
            window.history.replaceState({}, '', url);
          }}
        />
      }
      mapContent={
        <UsageMapComponent
          ref={mapRef}
          mapInstanceRef={mapInstanceRef}
          presentationModeiFrame={presentationModeiFrame}
          isLoading={isLoading}
          pins={filteredPins}
          layers={layers}
          selectedBaseMap={selectedBaseMap}
          onBaseMapChange={(name) => {
            setSelectedBaseMap(name);
            mapRef.current?.changeBaseMap?.(name);
          }}
          isParcelBoundariesVisible={isParcelBoundariesVisible}
          topLayerRef={topLayerRef}
          onParcelToggle={(visible) => {
            setIsParcelBoundariesVisible(visible);
            mapRef.current?.toggleParcelBoundaries?.(visible);
          }}
          onMapReady={handleMapReady}
          onPinSelect={handlePinSelect}
          selectedPin={selectedPin}
          onSelectionInvalidated={handleSelectionInvalidated}
          onCircleAnalysisChange={handleCircleAnalysisChange}
          onForceRightSidebarOpen={handleForceRightSidebarOpen}
        />
      }
      rightContent={
        <UsageSideBarRight
          selectedPin={selectedPin}
          clearSelection={() => {
            setSelectedPin(null);
            closeRightSidebarRef.current?.();
          }}
        />
      }
    />
  );
}
